/**
 * NoteWithCopy — renders markdown note with:
 *   - A "Copy all" button at the top right (inside the card, above sections)
 *   - A hover-revealed copy icon on each section (position:absolute inside the section)
 *
 * Each section is position:relative. The copy icon sits at top-right of that
 * block, fully contained — no overflow outside the card boundary.
 */
import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Copy, Check } from 'lucide-react';

function useCopyButton() {
  const [active, setActive] = useState(false);
  const copy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setActive(true);
      setTimeout(() => setActive(false), 1800);
    }).catch(() => {});
  }, []);
  return [active, copy];
}

function SectionCopyBtn({ text }) {
  const [active, copy] = useCopyButton();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => copy(text)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Copy section"
      style={{
        position: 'absolute',
        top: 4,
        right: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: hovered ? '#f3f4f6' : 'none',
        border: 'none',
        borderRadius: 4,
        padding: '2px 4px',
        cursor: 'pointer',
        opacity: hovered || active ? 1 : 0,
        transition: 'opacity 0.15s, background 0.15s',
      }}
      className="section-copy-btn"
    >
      {active
        ? <Check size={13} color="#16a34a" />
        : <Copy size={13} color="#6b7280" />}
    </button>
  );
}

function parseSections(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const sections = [];
  let current = null;

  for (const line of lines) {
    if (/^#{1,6}\s/.test(line)) {
      if (current) sections.push(current);
      current = { heading: line, bodyLines: [] };
    } else {
      if (current) {
        current.bodyLines.push(line);
      } else {
        current = { heading: null, bodyLines: [line] };
      }
    }
  }
  if (current) sections.push(current);

  return sections.map(s => ({
    heading: s.heading,
    body: s.bodyLines.join('\n').trim(),
    raw: s.heading
      ? (s.bodyLines.length ? s.heading + '\n' + s.bodyLines.join('\n') : s.heading)
      : s.bodyLines.join('\n'),
  }));
}

// Tables are the one block that can't be squeezed into a narrow widget frame:
// wrap each in its own horizontally scrollable box instead of letting the
// columns compress to one character per line. No effect when there is room.
const MD_COMPONENTS = {
  table: ({ node, ...props }) => (
    <div className="md-table-scroll">
      <table {...props} />
    </div>
  ),
};

function stripMarkdown(text) {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1');
}

export function NoteWithCopy({ content }) {
  const [allActive, copyAll] = useCopyButton();

  if (!content || content === 'Waiting for input...') {
    return <p className="empty-state">{content || 'Waiting for input...'}</p>;
  }

  const sections = parseSections(content);
  const plainAll = stripMarkdown(content);

  return (
    <div>
      {/* Copy-all row — own line above sections, right-aligned inside card */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          onClick={() => copyAll(plainAll)}
          title="Copy entire note"
          style={copyAllBtnStyle}
          className="note-copy-btn note-copy-btn--all"
        >
          {allActive
            ? <><Check size={13} color="#16a34a" /><span>Copied</span></>
            : <><Copy size={13} /><span>Copy all</span></>}
        </button>
      </div>

      {/* Sections — each is position:relative so the copy icon is contained */}
      {sections.map((sec, i) => {
        const sectionPlain = stripMarkdown(sec.raw);
        return (
          <div
            key={i}
            style={{ position: 'relative', paddingRight: 24, marginBottom: 4 }}
            className="note-section"
          >
            {sec.heading && <ReactMarkdown components={MD_COMPONENTS}>{sec.heading}</ReactMarkdown>}
            {sec.body && <ReactMarkdown components={MD_COMPONENTS}>{sec.body}</ReactMarkdown>}
            {sec.heading && <SectionCopyBtn text={sectionPlain} />}
          </div>
        );
      })}
    </div>
  );
}

const copyAllBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'none',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '3px 8px',
  fontSize: 11,
  color: '#6b7280',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
};
